/**
 * Shared Evidence sub-agent inline status chrome — the "not streaming yet"
 * visuals (starting/searching, cancelled, no-output, error) that both
 * `EvidenceSessionInlineBlock` and `EvidenceChildRunInline` need, extracted
 * once so they can't silently drift apart.
 */

import { projectRunFailureMessages } from '@heidi/agents/data';
import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AlertTriangle, Ban, Loader2 } from 'lucide-react';
import { useIntl } from 'react-intl';

import type { EvidenceSessionStatus } from '../EvidenceSessionInlineBlock';

import type { JSX } from 'react';

export const QuestionCaption = ({ question }: { question: string }): JSX.Element => (
  <p className="break-words text-sm leading-snug text-text-secondary [overflow-wrap:anywhere]">
    {question}
  </p>
);

export const ErrorNotice = ({
  message,
  className,
}: {
  message?: string;
  className?: string;
}): JSX.Element => {
  const intl = useIntl();
  const displayMessage =
    message ?? intl.formatMessage(projectRunFailureMessages.evidenceSearchFailed);

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border border-validation-destructive-muted bg-validation-destructive-muted px-2.5 py-1.5 text-xs text-validation-destructive',
        className
      )}
    >
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
      <span className="break-words">{displayMessage}</span>
    </div>
  );
};

export const CancelledNotice = ({
  question,
  className,
}: {
  question?: string;
  className?: string;
}): JSX.Element => (
  <div
    className={cn(
      'flex flex-col gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs text-text-secondary',
      className
    )}
  >
    <div className="flex items-center gap-2">
      <Ban className="size-3.5 shrink-0 text-text-tertiary" />
      <TypographyCaption className="text-text-secondary">
        Evidence search cancelled.
      </TypographyCaption>
    </div>
    {question && <QuestionCaption question={question} />}
  </div>
);

export interface StartingPlaceholderProps {
  question: string | undefined;
  status: EvidenceSessionStatus;
  /** Friendly error string from the tool_result (legacy caller); falls back to a generic message. */
  errorMessage?: string;
  /** Whether the loader is actively spinning (new-path caller); default true matches both callers' prior behavior. */
  isLoading?: boolean;
  /** The step's Working row already says it is searching, so a running search draws nothing. */
  searchesInWorkingBlock?: boolean;
  className?: string;
}

export const StartingPlaceholder = ({
  question,
  status,
  errorMessage,
  isLoading = true,
  searchesInWorkingBlock = false,
  className,
}: StartingPlaceholderProps): JSX.Element | null => {
  if (status === 'error') {
    return <ErrorNotice message={errorMessage} className={className} />;
  }

  if (status === 'cancelled') {
    return <CancelledNotice question={question} className={className} />;
  }

  if (status === 'success') {
    return (
      <div className={cn('flex flex-col gap-1.5', className)}>
        <TypographyCaption className="text-text-tertiary">
          Evidence search completed with no streamed output.
        </TypographyCaption>
        {question && <QuestionCaption question={question} />}
      </div>
    );
  }

  if (searchesInWorkingBlock) return null;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-center gap-2 py-1">
        <Loader2
          className={cn('size-3.5 text-text-tertiary', isLoading && 'animate-spin')}
        />
        <TypographyCaption className="text-text-tertiary">
          Searching clinical sources…
        </TypographyCaption>
      </div>
      {question && <QuestionCaption question={question} />}
    </div>
  );
};
